/**
 * Trend Engine — a reusable catalog of current design trends.
 *
 * A trend declares which styles and moods it works with and the concrete
 * effects it asks the downstream pipeline for. Effects the current
 * factory/engine can express map onto real knobs (decoration pack, type
 * scale, image treatment); effects they can't yet express (glass panels,
 * layer overlap) are still recorded so the adapter can flag them as
 * future extension points instead of silently dropping them.
 */

export const TRENDS = {
  'modern-gradients': {
    id: 'modern-gradients', name: 'Modern Gradients',
    styles: ['modern', 'creative', 'bold'], moods: ['energetic', 'fresh', 'bold'],
    effects: { decorationPack: 'soft-minimal', gradientBackdrop: true },
  },
  glassmorphism: {
    id: 'glassmorphism', name: 'Glassmorphism',
    styles: ['modern', 'minimal', 'premium'], moods: ['premium', 'fresh', 'minimal'],
    effects: { decorationPack: 'soft-minimal', glassPanels: true, softShadows: true },
  },
  'organic-layouts': {
    id: 'organic-layouts', name: 'Organic Layouts',
    styles: ['creative', 'elegant', 'modern'], moods: ['warm', 'healthy', 'playful', 'fresh'],
    effects: { decorationPack: 'organic-flow' },
  },
  'floating-cards': {
    id: 'floating-cards', name: 'Floating Cards',
    styles: ['modern', 'corporate', 'creative'], moods: ['fresh', 'minimal', 'premium'],
    effects: { decorationPack: 'soft-minimal', cardSurfaces: true, softShadows: true },
  },
  'large-typography': {
    id: 'large-typography', name: 'Large Typography',
    styles: ['bold', 'street', 'editorial', 'modern'], moods: ['bold', 'energetic', 'minimal'],
    effects: { typeScale: 'dramatic' },
  },
  'editorial-style': {
    id: 'editorial-style', name: 'Editorial Style',
    styles: ['editorial', 'luxury', 'elegant', 'minimal'], moods: ['elegant', 'premium', 'minimal', 'luxury'],
    effects: { typeScale: 'subtle', decorationPack: 'clean', imageStyle: 'photo' },
  },
  'layer-overlap': {
    id: 'layer-overlap', name: 'Layer Overlap',
    styles: ['creative', 'street', 'bold'], moods: ['energetic', 'playful', 'bold'],
    effects: { decorationPack: 'geo-pop', objectOverlap: true },
  },
  'diagonal-sections': {
    id: 'diagonal-sections', name: 'Diagonal Sections',
    styles: ['bold', 'modern', 'creative'], moods: ['energetic', 'bold', 'fresh'],
    effects: { decorationPack: 'geo-pop', diagonalCut: true },
  },
  'soft-shadows': {
    id: 'soft-shadows', name: 'Soft Shadows',
    styles: ['modern', 'minimal', 'corporate', 'elegant'], moods: ['fresh', 'minimal', 'premium', 'healthy'],
    effects: { decorationPack: 'soft-minimal', softShadows: true },
  },
  'minimal-luxury': {
    id: 'minimal-luxury', name: 'Minimal Luxury',
    styles: ['luxury', 'minimal', 'elegant', 'premium'], moods: ['luxury', 'premium', 'elegant', 'minimal'],
    effects: { decorationPack: 'clean', typeScale: 'subtle', imageStyle: 'studio' },
  },
};

export const listTrends = () =>
  Object.values(TRENDS).map(({ id, name }) => ({ id, name }));

export const getTrend = (id) => {
  const trend = TRENDS[id];
  if (!trend) throw new Error(`Unknown trend "${id}"`);
  return trend;
};

/**
 * Pick up to `max` trends coherent with a style + mood, deterministically.
 * Trends matching both dimensions rank above single-dimension matches;
 * the RNG only breaks ties, so direction stays stable for a seed.
 */
export const pickTrends = (styleId, moodId, rng, max = 2) => {
  const scored = Object.values(TRENDS)
    .map((trend) => ({
      trend,
      score:
        (trend.styles.includes(styleId) ? 2 : 0) +
        (trend.moods.includes(moodId) ? 1 : 0) +
        rng() * 0.5,
    }))
    .filter(({ score }) => score >= 2) // at least a style match
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, max).map(({ trend }) => trend);
};
